import { useMutation } from "@tanstack/react-query";
import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Avatar, Box, Button, Checkbox, CssBaseline, FormControlLabel, Grid, TextField, Typography, useTheme } from "@mui/material";
import { Image } from "mui-image";
import Icon from "@mdi/react";
import { mdiEmailArrowRight, mdiLoginVariant } from "@mdi/js";
import { Copyright } from "../../../components/Copyright";
import { authenticationService } from "../../../services/authentication";
import type { SendPasswordResetEmailParameters } from "../../../services/authentication/types";
import { buildChangePasswordPageUrl, buildSignInPageUrl } from "../../../utils/BuildURLs";
import { SCHOOL_BACKGROUND_IMAGE, DEFAULT_SCHOOL_LOGO_URL } from "../../../utils/Constants";
import { borderRadiusStandard } from "../../../theme";
import { ToastError500 } from "../../../utils/UI/ToastError500";

export const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParameters] = useSearchParams();
  const initialEmailAddress = searchParameters.get("emailAddress");
  const [confirmed, setConfirmed] = useState(false);
  const [emailAddress, setEmailAddress] = useState(initialEmailAddress ?? "");
  const { mutate: sendPasswordResetEmail, isLoading: isSendingPasswordResetEmail } = useMutation<unknown, Error, SendPasswordResetEmailParameters>(
    ({ emailAddress }: SendPasswordResetEmailParameters) => authenticationService.sendPasswordResetEmail({ emailAddress }),
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendPasswordResetEmail(
      { emailAddress },
      {
        onSuccess: () => {
          toast.success(t("authentication.reset_password_page.reset_password_success_message", { email: emailAddress }));
          const changePasswordPageUrl = buildChangePasswordPageUrl({
            emailAddress,
          });
          navigate(changePasswordPageUrl);
        },
        onError: (error) => {
          if (error.name === "InternalServerException") {
            toast(<ToastError500 />);
          }
          if (error.name === "UserNotFoundException") {
            toast.error(t("authentication.reset_password_page.reset_passwor_not_found_email_message", { email: emailAddress }));
          }
          if (error.name === "InvalidParameterException") {
            toast.error(t("authentication.reset_password_page.reset_passwor_not_confirmed_email_message", { email: emailAddress }));
          }
        },
      },
    );
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setEmailAddress(event.target.value);
  };

  const handleCheckboxChange = () => {
    setConfirmed(!confirmed);
  };

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={12}
        sx={{
          backgroundImage: `url(${SCHOOL_BACKGROUND_IMAGE})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[50] : t.palette.grey[900]),
          backgroundPosition: "center",
        }}
      >
        <Grid container>
          <Grid xs={2} sm={0} />
          <Grid xs={12} sm={8} sx={{ mt: "50px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Box
              sx={{
                my: 8,
                mx: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar sx={{ width: 120, height: 120, m: 1, bgcolor: "secondary.main", border: 5, borderColor: "white" }}>
                <Image style={{ margin: 0, width: "100%", background: "white" }} src={DEFAULT_SCHOOL_LOGO_URL} />
              </Avatar>
              <Typography component="h1" variant="h4" sx={{ color: "white", mb: 3 }} fontWeight="700">
                {t("authentication.reset_password_page.title")}
              </Typography>
              <Box component="form" onSubmit={handleSubmit} sx={{ p: 4, borderRadius: borderRadiusStandard, mt: 1, background: "white", textAlign: "center" }}>
                <Typography component="h1" variant="h4" sx={{ mb: 3 }} fontWeight="700">
                  {t("authentication.reset_password_page.password_recovery")}
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t("authentication.reset_password_page.email_address")}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  defaultValue={initialEmailAddress ? initialEmailAddress : ""}
                  onChange={handleEmailChange}
                  disabled={isSendingPasswordResetEmail}
                  sx={{ ".MuiInputBase-root": { borderRadius: borderRadiusStandard } }}
                />
                <FormControlLabel
                  control={<Checkbox onClick={handleCheckboxChange} />}
                  label={t("authentication.reset_password_page.confirmation_message")}
                  sx={{ mt: 1 }}
                />
                <Typography sx={{ fontStyle: "italic", mt: 1 }}>{t("authentication.reset_password_page.message_for_students")}</Typography>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  onClick={(event) => {
                    event.currentTarget?.form?.current?.reportVaelidity();
                  }}
                  startIcon={<Icon path={mdiEmailArrowRight} size={1} />}
                  disabled={emailAddress === "" || !confirmed || isSendingPasswordResetEmail}
                  sx={{
                    ":hover": {
                      backgroundColor: theme.palette.ylecolor.main,
                      color: "white",
                    },
                    mt: 3,
                    mb: 2,
                    borderRadius: borderRadiusStandard,
                    backgroundColor: theme.palette.ylecolor.main,
                  }}
                >
                  {t("authentication.reset_password_page.reset_password")}
                </Button>
                <Copyright />
              </Box>
            </Box>
            <Box width="90%">
              <Button
                id="signIn"
                type="submit"
                fullWidth
                startIcon={<Icon path={mdiLoginVariant} size={1} />}
                onClick={() => navigate(buildSignInPageUrl({ email: emailAddress }))}
                sx={{
                  ":hover": {
                    backgroundColor: theme.palette.ylecolor.main,
                    color: "white",
                  },
                  mt: 3,
                  mb: 2,
                  borderRadius: borderRadiusStandard,
                  backgroundColor: "white",
                  color: theme.palette.ylecolor.main,
                }}
              >
                {t("authentication.reset_password_page.remember_password")}
              </Button>
            </Box>
          </Grid>
          <Grid xs={2} sm={0} />
        </Grid>
      </Grid>
    </Grid>
  );
};
